/**
 * TanStack Query hooks for roadmaps. One cache entry per roadmap holds the
 * whole detail (items + phases + templates); mutations patch that entry
 * optimistically so dragging feels instant, then reconcile with the server
 * response. On error the snapshot is restored and the entry refetched.
 */

import { addDaysISO, diffDays } from "@shared/dates";
import type {
  CreateItemInput,
  CreatePhaseInput,
  CreateRoadmapInput,
  PhaseTemplateInput,
  UpdateItemInput,
  UpdatePhaseInput,
  UpdateRoadmapInput,
} from "@shared/schemas";
import { toSnapshot } from "@shared/snapshot";
import type {
  ItemWithPhases,
  Phase,
  PhaseTemplate,
  Roadmap,
  RoadmapDetail,
  RoadmapSummary,
  RoadmapVersion,
  RoadmapVersionDetail,
} from "@shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { stateAround, useHistoryStore } from "./history-store";

export const roadmapKeys = {
  list: ["roadmaps"] as const,
  detail: (id: string) => ["roadmap", id] as const,
  versions: (id: string) => ["roadmap", id, "versions"] as const,
  version: (id: string, versionId: string) => ["roadmap", id, "versions", versionId] as const,
};

export const roadmapsQuery = () => ({
  queryKey: roadmapKeys.list,
  queryFn: () => unwrap<RoadmapSummary[]>(api.roadmaps.$get()),
});

export const roadmapQuery = (id: string) => ({
  queryKey: roadmapKeys.detail(id),
  queryFn: () => unwrap<RoadmapDetail>(api.roadmaps[":id"].$get({ param: { id } })),
});

/** Public read-only roadmap behind a share token (no session). */
export const sharedRoadmapQuery = (token: string) => ({
  queryKey: ["shared", token] as const,
  queryFn: () => unwrap<RoadmapDetail>(api.shared[":token"].$get({ param: { token } })),
  staleTime: 30_000,
});

export function useRoadmaps() {
  return useQuery(roadmapsQuery());
}

export function useRoadmap(id: string) {
  return useQuery(roadmapQuery(id));
}

// ------------------------------------------------------------ cache helpers

type Updater = (detail: RoadmapDetail) => RoadmapDetail;

function patchDetail(qc: QueryClient, roadmapId: string, update: Updater) {
  qc.setQueryData<RoadmapDetail>(roadmapKeys.detail(roadmapId), (old) => (old ? update(old) : old));
}

function replaceItem(item: ItemWithPhases): Updater {
  return (d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? item : i)) });
}

function mapItem(itemId: string, fn: (i: ItemWithPhases) => ItemWithPhases): Updater {
  return (d) => ({ ...d, items: d.items.map((i) => (i.id === itemId ? fn(i) : i)) });
}

function mapPhase(phaseId: string, fn: (p: Phase) => Phase): Updater {
  return (d) => ({
    ...d,
    items: d.items.map((i) =>
      i.phases.some((p) => p.id === phaseId)
        ? { ...i, phases: i.phases.map((p) => (p.id === phaseId ? fn(p) : p)) }
        : i,
    ),
  });
}

/** Insert (or move) an item right after `afterId`, appending when absent, and renumber. */
function insertItem(item: ItemWithPhases, afterId?: string): Updater {
  return (d) => {
    const list = d.items.filter((i) => i.id !== item.id);
    const at = afterId ? list.findIndex((i) => i.id === afterId) : -1;
    list.splice(at === -1 ? list.length : at + 1, 0, item);
    return { ...d, items: list.map((i, position) => ({ ...i, position })) };
  };
}

/** Roadmaps for which a server response was skipped because newer edits were still in flight. */
const pendingRefetch = new Set<string>();

/**
 * Requests for one roadmap go out one at a time, in the order they were made.
 * The UI never waits for this (optimistic updates happen on `mutate`), but the
 * server then sees edits, undos and restores in the order the user made them:
 * a drag followed by an undo can never land the other way round.
 */
const queues = new Map<string, Promise<unknown>>();
function enqueue<T>(roadmapId: string, run: () => Promise<T>): Promise<T> {
  const previous = queues.get(roadmapId) ?? Promise.resolve();
  const next = previous.then(run, run);
  queues.set(
    roadmapId,
    next.then(
      () => undefined,
      () => undefined,
    ),
  );
  return next;
}

const newEntryId = () => Math.random().toString(36).slice(2, 10);

/** Title of an item (or "item") for history labels. */
const titleOf = (d: RoadmapDetail | undefined, itemId: string) =>
  d?.items.find((i) => i.id === itemId)?.title ?? "item";
const phaseOf = (d: RoadmapDetail | undefined, phaseId: string) =>
  d?.items.flatMap((i) => i.phases).find((p) => p.id === phaseId)?.name ?? "phase";
const q = (s: string) => `“${s}”`;

/**
 * Standard optimistic mutation wiring: snapshot, apply, rollback on error.
 * `onSuccess` receives the server entity to reconcile the cache precisely.
 *
 * Concurrency: if another mutation for the same roadmap is still in flight when a
 * response arrives, that response is NOT written to the cache (it would overwrite the
 * newer optimistic state and make bars snap back). Instead the roadmap is refetched
 * once the last in-flight mutation settles.
 *
 * History (undo/redo): when `history` returns a label, the mutation records an
 * entry whose updater is the same pure function the cache gets. Optimistic
 * mutations know it at once; the others (creates) get it when the server
 * answers, and the entry stays pending until then. A failed mutation drops its
 * entry. See history-store.ts for how entries fold into states.
 */
function useOptimistic<TVars, TResult>(
  roadmapId: string,
  opts: {
    mutationFn: (vars: TVars) => Promise<TResult>;
    optimistic?: (vars: TVars) => Updater | undefined;
    reconcile?: (result: TResult, vars: TVars) => Updater | undefined;
    errorMessage?: string;
    /** Label for the undo history, or null to leave this mutation out of it. */
    history?: (vars: TVars, before: RoadmapDetail) => string | null;
  },
) {
  const qc = useQueryClient();
  const key = roadmapKeys.detail(roadmapId);
  const inFlight = () => qc.isMutating({ mutationKey: key });
  return useMutation({
    mutationKey: key,
    mutationFn: (vars: TVars) => enqueue(roadmapId, () => opts.mutationFn(vars)),
    onMutate: async (vars) => {
      const update = opts.optimistic?.(vars);
      // Only cancel refetches when we are about to write optimistic state they could clobber.
      if (update) await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueryData<RoadmapDetail>(key);
      const label = snapshot && opts.history ? opts.history(vars, snapshot) : null;
      let entryId: string | undefined;
      if (snapshot && label) {
        entryId = newEntryId();
        useHistoryStore
          .getState()
          .push(roadmapId, snapshot, { id: entryId, label, update: update ?? null });
      }
      if (!update) return { snapshot: undefined, entryId };
      patchDetail(qc, roadmapId, update);
      return { snapshot, entryId };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(key, ctx.snapshot);
      if (ctx?.entryId) useHistoryStore.getState().remove(ctx.entryId);
      toast.error(opts.errorMessage ?? "Something went wrong", {
        description: err instanceof Error ? err.message : undefined,
      });
      void qc.invalidateQueries({ queryKey: key });
    },
    onSuccess: (result, vars, ctx) => {
      const update = opts.reconcile?.(result, vars);
      if (ctx?.entryId) {
        const history = useHistoryStore.getState();
        const pending = history.entries.find((e) => e.id === ctx.entryId && !e.update);
        if (pending && update) history.settle(ctx.entryId, update);
        else if (pending) history.remove(ctx.entryId); // nothing to fold: cannot be undone
      }
      // This mutation is still counted as pending here, hence "> 1".
      if (inFlight() > 1) {
        pendingRefetch.add(roadmapId);
        return;
      }
      if (update) patchDetail(qc, roadmapId, update);
    },
    onSettled: () => {
      if (inFlight() === 1 && pendingRefetch.has(roadmapId)) {
        pendingRefetch.delete(roadmapId);
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}

// ------------------------------------------------------------ roadmaps

export function useCreateRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoadmapInput) => unwrap<Roadmap>(api.roadmaps.$post({ json: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: roadmapKeys.list }),
  });
}

export function useCreateDemoRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap<Roadmap>(api.roadmaps.demo.$post()),
    onSuccess: () => qc.invalidateQueries({ queryKey: roadmapKeys.list }),
  });
}

export function useDeleteRoadmap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<{ ok: true }>(api.roadmaps[":id"].$delete({ param: { id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: roadmapKeys.list }),
  });
}

export function useUpdateRoadmap(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (patch: UpdateRoadmapInput) =>
      unwrap<Roadmap>(api.roadmaps[":id"].$patch({ param: { id: roadmapId }, json: patch })),
    optimistic: (patch) => (d) => ({ ...d, roadmap: { ...d.roadmap, ...patch } }),
    reconcile: (roadmap) => (d) => ({ ...d, roadmap }),
    errorMessage: "Could not update roadmap",
    history: (patch) => (patch.name !== undefined ? "Rename roadmap" : "Edit roadmap"),
  });
}

/** Turn the public share link on/off; the server returns the roadmap with its token. */
export function useSetShare(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (enabled: boolean) =>
      unwrap<Roadmap>(
        api.roadmaps[":id"].share.$post({ param: { id: roadmapId }, json: { enabled } }),
      ),
    reconcile: (roadmap) => (d) => ({ ...d, roadmap }),
    errorMessage: "Could not update sharing",
  });
}

export function useReplacePhaseTemplates(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (templates: PhaseTemplateInput[]) =>
      unwrap<PhaseTemplate[]>(
        api.roadmaps[":id"]["phase-templates"].$put({
          param: { id: roadmapId },
          json: { templates },
        }),
      ),
    reconcile: (phaseTemplates) => (d) => ({ ...d, phaseTemplates }),
    errorMessage: "Could not save phase templates",
    history: () => "Edit phase templates",
  });
}

// ------------------------------------------------------------ items

export function useCreateItem(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (input: CreateItemInput) =>
      unwrap<ItemWithPhases>(
        api.roadmaps[":id"].items.$post({ param: { id: roadmapId }, json: input }),
      ),
    reconcile: (item, input) => insertItem(item, input.afterItemId),
    errorMessage: "Could not create item",
    history: () => "Add item",
  });
}

export function useUpdateItem(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ id, ...patch }: UpdateItemInput & { id: string }) =>
      unwrap<ItemWithPhases>(api.items[":id"].$patch({ param: { id }, json: patch })),
    optimistic:
      ({ id, shiftPhases, ...patch }) =>
      (d) =>
        mapItem(id, (item) => {
          const next = { ...item, ...patch };
          if (shiftPhases && patch.startDate) {
            const delta = diffDays(item.startDate, patch.startDate);
            if (delta !== 0) {
              next.phases = item.phases.map((p) => ({
                ...p,
                startDate: addDaysISO(p.startDate, delta),
                endDate: addDaysISO(p.endDate, delta),
              }));
            }
          }
          return next;
        })(d),
    reconcile: (item) => replaceItem(item),
    errorMessage: "Could not update item",
    history: ({ id, shiftPhases, ...patch }, before) => {
      const keys = Object.keys(patch).filter((k) => k !== "collapsed");
      if (keys.length === 0) return null; // collapsing is view state
      const title = q(titleOf(before, id));
      if (patch.title !== undefined) return `Rename ${title}`;
      if (patch.color !== undefined) return `Recolor ${title}`;
      if (patch.description !== undefined) return `Edit notes of ${title}`;
      return shiftPhases ? `Move ${title}` : `Change dates of ${title}`;
    },
  });
}

export function useDeleteItem(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (id: string) => unwrap<{ ok: true }>(api.items[":id"].$delete({ param: { id } })),
    optimistic: (id) => (d) => ({ ...d, items: d.items.filter((i) => i.id !== id) }),
    errorMessage: "Could not delete item",
    history: (id, before) => `Delete ${q(titleOf(before, id))}`,
  });
}

export function useDuplicateItem(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (id: string) =>
      unwrap<ItemWithPhases>(api.items[":id"].duplicate.$post({ param: { id } })),
    reconcile: (copy, sourceId) => insertItem(copy, sourceId),
    errorMessage: "Could not duplicate item",
    history: (id, before) => `Duplicate ${q(titleOf(before, id))}`,
  });
}

export function useReorderItems(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (itemIds: string[]) =>
      unwrap<ItemWithPhases[]>(
        api.roadmaps[":id"].items.reorder.$post({ param: { id: roadmapId }, json: { itemIds } }),
      ),
    optimistic: (itemIds) => (d) => {
      const byId = new Map(d.items.map((i) => [i.id, i]));
      const ordered = itemIds.map((id) => byId.get(id)).filter(Boolean) as ItemWithPhases[];
      const rest = d.items.filter((i) => !itemIds.includes(i.id));
      return { ...d, items: [...ordered, ...rest].map((i, position) => ({ ...i, position })) };
    },
    reconcile: (items) => (d) => ({ ...d, items }),
    errorMessage: "Could not reorder items",
    history: () => "Reorder items",
  });
}

export function useCollapseAll(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (collapsed: boolean) =>
      unwrap<ItemWithPhases[]>(
        api.roadmaps[":id"].items.collapse.$post({
          param: { id: roadmapId },
          json: { collapsed },
        }),
      ),
    optimistic: (collapsed) => (d) => ({
      ...d,
      items: d.items.map((i) => ({ ...i, collapsed })),
    }),
    reconcile: (items) => (d) => ({ ...d, items }),
    errorMessage: "Could not update items",
  });
}

// ------------------------------------------------------------ phases

export function useCreatePhase(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ itemId, ...input }: CreatePhaseInput & { itemId: string }) =>
      unwrap<Phase>(api.items[":id"].phases.$post({ param: { id: itemId }, json: input })),
    reconcile: (phase) => mapItem(phase.itemId, (i) => ({ ...i, phases: [...i.phases, phase] })),
    errorMessage: "Could not add phase",
    history: (input, before) => `Add ${q(input.name)} to ${q(titleOf(before, input.itemId))}`,
  });
}

export function useAddDefaultPhases(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: (itemId: string) =>
      unwrap<ItemWithPhases>(api.items[":id"].phases.defaults.$post({ param: { id: itemId } })),
    reconcile: (item) => replaceItem(item),
    errorMessage: "Could not add phases",
    history: (itemId, before) => `Add default phases to ${q(titleOf(before, itemId))}`,
  });
}

export function useUpdatePhase(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ id, ...patch }: UpdatePhaseInput & { id: string }) =>
      unwrap<Phase>(api.phases[":id"].$patch({ param: { id }, json: patch })),
    optimistic:
      ({ id, ...patch }) =>
      (d) =>
        mapPhase(id, (p) => ({ ...p, ...patch }))(d),
    reconcile: (phase) => mapPhase(phase.id, () => phase),
    errorMessage: "Could not update phase",
    history: ({ id, ...patch }, before) => {
      const name = q(phaseOf(before, id));
      if (patch.name !== undefined) return `Rename ${name}`;
      if (patch.color !== undefined) return `Recolor ${name}`;
      return `Change dates of ${name}`;
    },
  });
}

export function useDeletePhase(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ id }: { id: string; itemId: string }) =>
      unwrap<{ ok: true }>(api.phases[":id"].$delete({ param: { id } })),
    optimistic:
      ({ id, itemId }) =>
      (d) =>
        mapItem(itemId, (i) => ({ ...i, phases: i.phases.filter((p) => p.id !== id) }))(d),
    errorMessage: "Could not delete phase",
    history: ({ id }, before) => `Delete ${q(phaseOf(before, id))}`,
  });
}

/**
 * Delete a phase immediately and offer "Undo" in a toast, which re-creates it
 * (with a new id, appended to the item). Cheaper and friendlier than a confirm dialog.
 */
export function useDeletePhaseWithUndo(roadmapId: string) {
  const deletePhase = useDeletePhase(roadmapId);
  const createPhase = useCreatePhase(roadmapId);
  return (phase: Phase) => {
    const toastId = toast(`Removed “${phase.name}”`, {
      action: {
        label: "Undo",
        onClick: () =>
          createPhase.mutate({
            itemId: phase.itemId,
            name: phase.name,
            color: phase.color,
            startDate: phase.startDate,
            endDate: phase.endDate,
          }),
      },
    });
    deletePhase.mutate(
      { id: phase.id, itemId: phase.itemId },
      // If the delete failed the phase is still there; offering Undo would duplicate it.
      { onError: () => toast.dismiss(toastId) },
    );
  };
}

export function useReorderPhases(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ itemId, phaseIds }: { itemId: string; phaseIds: string[] }) =>
      unwrap<Phase[]>(
        api.items[":id"].phases.reorder.$post({ param: { id: itemId }, json: { phaseIds } }),
      ),
    optimistic:
      ({ itemId, phaseIds }) =>
      (d) =>
        mapItem(itemId, (i) => {
          const byId = new Map(i.phases.map((p) => [p.id, p]));
          const ordered = phaseIds.map((id) => byId.get(id)).filter(Boolean) as Phase[];
          const rest = i.phases.filter((p) => !phaseIds.includes(p.id));
          return { ...i, phases: [...ordered, ...rest] };
        })(d),
    reconcile:
      (phases, { itemId }) =>
      (d) =>
        mapItem(itemId, (i) => ({ ...i, phases }))(d),
    errorMessage: "Could not reorder phases",
    history: ({ itemId }, before) => `Reorder phases of ${q(titleOf(before, itemId))}`,
  });
}

// ------------------------------------------------------------ undo / redo

/**
 * Restoring a snapshot keeps whatever is collapsed right now (view state) and
 * the roadmap's non-content fields (share token, timestamps).
 */
function mergeRestore(current: RoadmapDetail, target: RoadmapDetail): RoadmapDetail {
  const collapsed = new Map(current.items.map((i) => [i.id, i.collapsed]));
  return {
    roadmap: {
      ...current.roadmap,
      name: target.roadmap.name,
      description: target.roadmap.description,
    },
    items: target.items.map((i, position) => ({
      ...i,
      position,
      collapsed: collapsed.get(i.id) ?? i.collapsed,
    })),
    phaseTemplates: target.phaseTemplates,
  };
}

/**
 * Replace the roadmap's content with a snapshot (the transport behind undo/redo).
 * Queued like every other mutation, so it lands after the edits made before it.
 */
function useRestoreContent(roadmapId: string) {
  return useOptimistic(roadmapId, {
    mutationFn: ({ target }: { target: RoadmapDetail }) =>
      unwrap<RoadmapDetail>(
        api.roadmaps[":id"].content.$put({ param: { id: roadmapId }, json: toSnapshot(target) }),
      ),
    optimistic:
      ({ target }) =>
      (d) =>
        mergeRestore(d, target),
    reconcile: (detail) => (d) => mergeRestore(d, detail),
    errorMessage: "Could not undo",
  });
}

/**
 * History steps for a roadmap run one after another, each until its restore
 * has settled. A step may have to wait for a pending create before it can
 * restore, and a failed restore has to put the cursor back; the next step must
 * not move the cursor before either, or the roadmap and the cursor would drift
 * one step apart.
 */
const historySteps = new Map<string, Promise<void>>();

export function useUndoRedo(roadmapId: string) {
  const { mutateAsync: restore } = useRestoreContent(roadmapId);
  const canUndo = useHistoryStore((s) => s.roadmapId === roadmapId && s.cursor > 0);
  const canRedo = useHistoryStore((s) => s.roadmapId === roadmapId && s.cursor < s.entries.length);
  const step = useCallback(
    (direction: "undo" | "redo") => {
      const run = async () => {
        const history = useHistoryStore.getState();
        if (history.roadmapId !== roadmapId) return;
        const entry = direction === "undo" ? history.stepBack() : history.stepForward();
        if (!entry) return;
        toast(`${direction === "undo" ? "Undid" : "Redid"}: ${entry.label}`, {
          duration: 1800,
          id: "history",
        });
        // The state to bring back is the fold before (undo) or after (redo) the entry.
        // A create the server has not answered yet has nothing to fold: wait for it.
        const target = await stateAround(
          roadmapId,
          entry.id,
          direction === "undo" ? "before" : "after",
        );
        if (!target) return;
        try {
          // Awaited here, so a failure moves the cursor back for *this* step before
          // the next queued step can touch it.
          await restore({ target });
        } catch {
          // The cache was rolled back by the mutation itself; only the cursor is left.
          const s = useHistoryStore.getState();
          if (direction === "undo") s.stepForward();
          else s.stepBack();
        }
      };
      const previous = historySteps.get(roadmapId) ?? Promise.resolve();
      const next = previous.then(run, run);
      historySteps.set(
        roadmapId,
        next.then(
          () => undefined,
          () => undefined,
        ),
      );
    },
    [roadmapId, restore],
  );
  const undo = useCallback(() => step("undo"), [step]);
  const redo = useCallback(() => step("redo"), [step]);
  return { undo, redo, canUndo, canRedo };
}

// ------------------------------------------------------------ version history

export const versionsQuery = (roadmapId: string) => ({
  queryKey: roadmapKeys.versions(roadmapId),
  queryFn: () =>
    unwrap<RoadmapVersion[]>(api.roadmaps[":id"].versions.$get({ param: { id: roadmapId } })),
});

export function useVersions(roadmapId: string, enabled = true) {
  return useQuery({ ...versionsQuery(roadmapId), enabled, staleTime: 0 });
}

/** One version with its snapshot, for the "what changed" summary. */
export function useVersion(roadmapId: string, versionId: string | null) {
  return useQuery({
    queryKey: roadmapKeys.version(roadmapId, versionId ?? ""),
    queryFn: () =>
      unwrap<RoadmapVersionDetail>(
        api.roadmaps[":id"].versions[":versionId"].$get({
          param: { id: roadmapId, versionId: versionId ?? "" },
        }),
      ),
    enabled: !!versionId,
    staleTime: Number.POSITIVE_INFINITY, // a version never changes
  });
}

export function useCreateVersion(roadmapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label?: string) =>
      unwrap<RoadmapVersion>(
        api.roadmaps[":id"].versions.$post({
          param: { id: roadmapId },
          json: label ? { label } : {},
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: roadmapKeys.versions(roadmapId) }),
    onError: (err) =>
      toast.error("Could not save version", {
        description: err instanceof Error ? err.message : undefined,
      }),
  });
}

export function useDeleteVersion(roadmapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      unwrap<{ ok: true }>(
        api.roadmaps[":id"].versions[":versionId"].$delete({
          param: { id: roadmapId, versionId },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: roadmapKeys.versions(roadmapId) }),
    onError: (err) =>
      toast.error("Could not delete version", {
        description: err instanceof Error ? err.message : undefined,
      }),
  });
}

/** Bring a version back. Undoable like any edit; the list refreshes ("Before restore" appears). */
export function useRestoreVersion(roadmapId: string) {
  const qc = useQueryClient();
  const m = useOptimistic(roadmapId, {
    mutationFn: (versionId: string) =>
      unwrap<RoadmapDetail>(
        api.roadmaps[":id"].versions[":versionId"].restore.$post({
          param: { id: roadmapId, versionId },
        }),
      ),
    reconcile: (detail) => (d) => mergeRestore(d, detail),
    errorMessage: "Could not restore version",
    history: () => "Restore version",
  });
  const { mutate } = m;
  const restore = useCallback(
    (versionId: string, onSuccess?: () => void) =>
      mutate(versionId, {
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: roadmapKeys.versions(roadmapId) });
          onSuccess?.();
        },
      }),
    [mutate, qc, roadmapId],
  );
  return { restore, isPending: m.isPending };
}
